// Structured workout: blocks as a fraction of FTP (or in watts), played in ERG mode.
import { estimateLoad, flatten, blockFrac, type Workout } from '@gruvetto/core';
import type { CreationOf, WorkoutContent } from '@gruvetto/contracts';
import { defineKind, err, warn, fmtMin, type KindContext } from '../kind.js';

/** Workout in the engine format (ERG playback, profile, TSS). */
export const toWorkout = (id: string, c: { name: string; desc?: string; content: WorkoutContent }): Workout => ({ id, name: c.name, desc: c.desc, cat: c.content.cat, test: c.content.test, segs: c.content.segs });

/** Physiological checks shared by standalone workouts and the workouts of a plan. */
export function checkWorkout(w: Workout, ctx: KindContext, where = ''): ReturnType<typeof err>[] {
  const out = [], at = where ? `${where}: ` : '';
  const ld = estimateLoad(w, ctx.ftp), blocks = flatten(w);
  if (ld.duration < 5 * 60) out.push(err(`${at}workout too short (${fmtMin(ld.duration)}, 5 min at least)`));
  if (ld.duration > 6 * 3600) out.push(err(`${at}workout too long (${fmtMin(ld.duration)}, 6 h at most)`));
  let hard = 0;
  for (const b of blocks) {
    const f = blockFrac(b, ctx.ftp);
    if (f > 1.5 && b.dur > 60) out.push(warn(`${at}${fmtMin(b.dur)} block at ${Math.round(f * 100)}% of FTP: unsustainable beyond a minute`));
    if (f > 1.05) hard += b.dur;
  }
  if (hard > 40 * 60) out.push(warn(`${at}${fmtMin(hard)} above threshold: very high load`));
  if (!w.test && blocks[0] && blockFrac(blocks[0], ctx.ftp) > 0.8) out.push(warn(`${at}no warm-up: the first block is already intense`));
  return out;
}

export const workoutKind = defineKind({
  kind: 'workout', label: 'Workout', plural: 'Workouts', icon: '📈', verb: 'Ride',
  check: (c, ctx) => checkWorkout(toWorkout('x', c), ctx),
  facts(c, ctx) {
    const ld = estimateLoad(toWorkout('x', c), ctx.ftp);
    return [{ label: 'Duration', value: fmtMin(ld.duration) }, { label: 'TSS', value: String(ld.TSS) }, { label: 'IF', value: ld.IF.toFixed(2) }];
  },
  ai: {
    summary: 'a structured trainer workout (intervals, steps, in % of FTP)',
    effort: 'medium', maxTokens: 16000,
    brief: `A workout is played in ERG mode: the trainer imposes the power of each block.
- Intensities as a fraction of FTP (p = 0.75 for 75%). Use "w" (absolute watts) only for a test.
- Blocks: steady (step), ramp (p0 → p1), intervals (reps × on/off), free (free ride, no ERG).
- Always a warm-up (5 to 15 min, ramp 0.45 → 0.7) and a cool-down (descending ramp).
- Zones: recovery < 0.55, endurance 0.55–0.75, tempo 0.75–0.9, threshold 0.9–1.05, VO2max 1.05–1.2, anaerobic 1.2–1.5, sprint > 1.5 (10 to 30 s at most).
- "text": short instruction shown during the block ("Cadence 95, stay seated"). "cad": optional target cadence.
- cat among: endurance, tempo, seuil, vo2, anaerobie, recup, test (ids kept as is).
- Realistic total duration for the request; respect the requested duration within ±5%.`,
    example: {
      kind: 'workout', name: 'Pyramid tempo 50 min', desc: 'Three increasing tempo blocks to build strength endurance.', tags: ['tempo', 'endurance'],
      content: {
        cat: 'tempo',
        segs: [
          { kind: 'ramp', dur: 600, p0: 0.45, p1: 0.7, text: 'Progressive warm-up' },
          { kind: 'steady', dur: 480, p: 0.8, cad: 85, text: 'Tempo, cadence 85' },
          { kind: 'steady', dur: 180, p: 0.55 },
          { kind: 'steady', dur: 600, p: 0.85, text: 'Sustained tempo' },
          { kind: 'steady', dur: 180, p: 0.55 },
          { kind: 'intervals', reps: 4, on: { dur: 60, p: 1.1 }, off: { dur: 60, p: 0.6 }, text: 'Surges' },
          { kind: 'ramp', dur: 480, p0: 0.65, p1: 0.4, text: 'Cool-down' },
        ],
      },
    } satisfies CreationOf<'workout'>,
  },
});
